/**
 * Melora: <size-chart> drives the size chart modal rendered by snippets/size-chart.liquid.
 *
 * Progressive enhancement. Liquid prints every cell's centimetre value into both the text and a data-cm
 * attribute, so the table is already complete and correct without this script. All this element does is
 * switch tabs and rewrite the displayed numbers when the unit changes.
 */

// Imported through the theme's importmap (layout/theme.liquid), so the URL carries the asset version and an
// edit here is never served from a stale browser cache the way a relative path would be
import { fromCm, roundTwo } from 'measurement-units';

const MEDIA_TAB = 'how-to-measure';

class SizeChart extends HTMLElement {
  #initialised = false;
  #unit = 'cm';

  connectedCallback() {
    // <x-modal> moves itself to <body> while open and back when closed, which reconnects this element
    if (this.#initialised) {
      return;
    }

    this.#initialised = true;
    this.modal = this.closest('x-modal');

    this.addEventListener('click', this.#onClick.bind(this));
    this.addEventListener('change', this.#onChange.bind(this));
    this.modal?.addEventListener('dialog:before-hide', this.#onBeforeHide.bind(this));
  }

  get #tabs() {
    return Array.from(this.querySelectorAll('[data-tab]'));
  }

  #onClick(event) {
    const tab = event.target.closest('[data-tab]');

    if (tab) {
      this.#selectTab(tab.getAttribute('data-tab'));
    }
  }

  #selectTab(name) {
    this.#tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.getAttribute('data-tab') === name)));

    this.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.hidden = panel.getAttribute('data-panel') !== name;
    });

    // The measuring guide has no numbers, so the unit choice does not apply to it
    const units = this.querySelector('[data-units]');

    if (units) {
      units.hidden = name === MEDIA_TAB;
    }
  }

  #onChange(event) {
    if (event.target.matches('[data-unit]')) {
      this.#unit = event.target.value;
      this.#renderValues();
    }
  }

  #onBeforeHide() {
    // A two minute clip should not keep playing into a closed modal
    this.querySelector('video')?.pause();
  }

  #renderValues() {
    this.querySelectorAll('[data-cm]').forEach((cell) => {
      const valueCm = parseFloat(cell.dataset.cm);

      if (Number.isFinite(valueCm)) {
        // String() drops the trailing zero, so 25.20 reads as 25.2 the way the chart is written
        cell.textContent = String(roundTwo(fromCm(valueCm, this.#unit)));
      }
    });
  }
}

if (!window.customElements.get('size-chart')) {
  window.customElements.define('size-chart', SizeChart);
}
